import React, { useContext, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  MenuItem,
  Switch,
} from '@mui/material';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import { useTranslation } from 'react-i18next';
import { WithScrollbars } from '@eposnow/ui-core';
import { ScrollbarsAutoHideBehavior, ScrollbarsVisibilityBehavior } from 'overlayscrollbars';
import { UIContext } from '../../../../context/UIContext';
import { SidekickDataGridConfigContext } from '../../../../context/SidekickDataGridConfigContext';

export interface ColumnGroup {
  name: string;
  entries: string[];
}

const GridColumns = () => {
  const { t } = useTranslation();
  const { animationsDisabled, isMobile, colorMode } = useContext(UIContext);
  const {
    changeColumnVisibility,
    columnGroups,
    columnVisibilityModel,
    showColumnsButton,
    translationKey,
  } = useContext(SidekickDataGridConfigContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    changeColumnVisibility(event.target.name, event.target.checked);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Button
        aria-label={t('components.gridActionsMenu.columns')}
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        sx={{
          color: 'text.secondary',
          ':hover': {
            color: 'text.primary',
            backgroundColor: colorMode === 'light' ? 'grey.4p' : 'elevation.4dp',
          },
          ...(open && {
            color: 'primary.main',
            backgroundColor: colorMode === 'light' ? 'main.8p' : 'main.26p',
          }),
          padding: 1.5,
          borderRadius: '4px',
        }}
        onClick={handleClick}
        variant="text"
        startIcon={<ViewWeekOutlinedIcon />}
      >
        {t('components.gridActionsMenu.columns')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        id="row-menu"
        open={open}
        onClose={handleClose}
        MenuListProps={{
          disablePadding: true,
        }}
        transitionDuration={animationsDisabled ? 1 : 'auto'}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              marginLeft: '5px',
              minWidth: '100px',
              overflow: 'visible',
              textAlign: 'center',
              border: (theme) =>
                `1px ${
                  colorMode === 'light'
                    ? theme?.palette?.grey?.['12p']
                    : theme?.palette?.grey?.['23p']
                } solid`,
              backgroundImage:
                'linear-gradient(rgba(255, 255, 255, 0.10), rgba(255, 255, 255, 0.10))',
              filter: `drop-shadow(0px 2px 8px ${colorMode === 'light' ? 'grey.18p' : 'grey.87p'})`,
              mt: { xs: 1, sm: 1.5 },
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 10,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                backgroundImage:
                  'linear-gradient(rgba(255, 255, 255, 0.10), rgba(255, 255, 255, 0.10))',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
                borderTop: (theme) =>
                  `1px ${
                    colorMode === 'light'
                      ? theme?.palette?.grey?.['12p']
                      : theme?.palette?.grey?.['23p']
                  } solid`,
                borderLeft: (theme) =>
                  `1px ${
                    colorMode === 'light'
                      ? theme?.palette?.grey?.['12p']
                      : theme?.palette?.grey?.['23p']
                  } solid`,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <WithScrollbars
          style={{
            maxHeight: 'min(50vh, 400px)',
          }}
          options={{
            scrollbars: {
              theme: colorMode === 'light' ? 'os-theme-dark' : 'os-theme-light',
              visibility: 'auto' as ScrollbarsVisibilityBehavior,
              autoHide: 'move' as ScrollbarsAutoHideBehavior,
              dragScroll: true,
              clickScroll: true,
            },
          }}
        >
          {showColumnsButton &&
            !columnGroups &&
            Object.entries(columnVisibilityModel).map(([column, visible]) => (
              <MenuItem key={column} sx={{ paddingY: '0.25rem' }}>
                <FormControl component="fieldset" variant="standard">
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch checked={visible} onChange={handleChange} name={column} />}
                      label={t(`screens.products.sidekick.import.${translationKey}.${column}`)}
                    />
                  </FormGroup>
                </FormControl>
              </MenuItem>
            ))}
          {showColumnsButton &&
            columnGroups &&
            columnGroups.map((group, index) => (
              <React.Fragment key={`group-${group.name}`}>
                {index !== 0 && <Divider />}
                {group.entries.map((entry) => (
                  <MenuItem key={`${group.name}-${entry}`} sx={{ paddingY: '0.25rem' }}>
                    <FormControl component="fieldset" variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={columnVisibilityModel[entry]}
                              onChange={handleChange}
                              name={entry}
                            />
                          }
                          label={t(`screens.products.sidekick.import.${translationKey}.${entry}`)}
                        />
                      </FormGroup>
                    </FormControl>
                  </MenuItem>
                ))}
              </React.Fragment>
            ))}
        </WithScrollbars>
      </Menu>
    </Box>
  );
};

export default GridColumns;
